import { memo, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '../../../constants/routes';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePaginationState } from '../../../hooks/usePaginationState';
import { UserRolesCard } from './UserRolesCard';
import { UserStatusControl } from './UserStatusControl';
import { useUserDetailsQuery } from '../../../hooks/useDashboardQueries';
import { formatUserRole, getRoleBadgeClass } from '../../../shared/utils/statusUtils';
import { UserStatus } from '../../../shared/enums/status';
import { BalanceModal } from './components/BalanceModal';
import { MergeModal } from './components/MergeModal';
import { StartChatModal } from './components/StartChatModal';
import { SessionsPanel } from './components/SessionsPanel';
import { TransactionsPanel } from './components/TransactionsPanel';
import { formatNumericValue, getInitials } from './utils';
import { formatDate } from '../../../shared/utils/dateFormat';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';
import { UserOrganizationCard } from './UserOrganizationCard';
import './user-details.css';

type TabId = 'sessions' | 'transactions';

export const UserDetails = memo(function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('sessions');
  const sessionsPagination = usePaginationState();
  const transactionsPagination = usePaginationState();
  const [isBalanceModalOpen, setBalanceModalOpen] = useState(false);
  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [isChatModalOpen, setChatModalOpen] = useState(false);
  const { has } = usePermissions();

  const { data: user, isLoading, error, refetch } = useUserDetailsQuery(userId);

  const handleBack = useCallback(
    () => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_USERS}`),
    [navigate],
  );

  if (!userId) {
    return (
      <div className="user-details-page">
        <div className="user-details-container">
          <ErrorMessage message={t('dashboard.userDetails.missingUserId')} onRetry={handleBack} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="user-details-page">
        <div className="user-details-container">
          <div className="user-details-loading">{t('dashboard.userDetails.loadingUser')}</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="user-details-page">
        <div className="user-details-container">
          <ErrorMessage
            message={error instanceof Error ? error.message : t('dashboard.userDetails.loadFailed')}
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  const primary = user.email || user.phone || t('dashboard.userDetails.unknownUser');
  const hasEmailAndPhone = Boolean(user.email && user.phone);
  const avatarStatusClass =
    user.status === UserStatus.BLOCKED
      ? 'is-blocked'
      : user.status === UserStatus.INACTIVE
        ? 'is-inactive'
        : '';
  const balanceTone = user.balance < 0 ? 'is-negative' : user.balance === 0 ? 'is-zero' : '';
  const canManageUsers = has('users.manage');
  const canMessage = Boolean(user.telegramChatId) && has('support.manage');
  const canMerge = has('users.merge');

  return (
    <div className="user-details-page">
      {/* Mobile-only sticky top bar so back stays reachable while scrolling */}
      <div className="ud-mobile-topbar">
        <button
          type="button"
          className="identity-back"
          onClick={handleBack}
          aria-label={t('dashboard.userDetails.backAria')}
        >
          <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
        </button>
        <span className="ud-mobile-topbar-title">{t('dashboard.pageTitles.userDetails')}</span>
      </div>
      <div className="user-details-container">
        {/* Identity */}
        <header className="identity-card">
          <button
            type="button"
            className="identity-back"
            onClick={handleBack}
            aria-label={t('dashboard.userDetails.backAria')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
          </button>
          <div className={`identity-avatar ${avatarStatusClass}`} aria-hidden="true">
            {getInitials(user.email, user.phone)}
          </div>
          <div className="identity-body">
            <h1 className="identity-primary">{primary}</h1>
            <div className="identity-meta">
              {hasEmailAndPhone && (
                <span className="identity-meta-item">
                  <span className="material-symbols-outlined" aria-hidden="true">phone</span>
                  {user.phone}
                </span>
              )}
              {user.createdAt && (
                <span className="identity-meta-item">
                  <span className="material-symbols-outlined" aria-hidden="true">event</span>
                  {t('dashboard.userDetails.memberSince', { date: formatDate(user.createdAt) })}
                </span>
              )}
              <span className="identity-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">send</span>
                {user.telegramChatId
                  ? t('dashboard.userDetails.telegramConnected')
                  : t('dashboard.userDetails.telegramNotLinked')}
              </span>
              <span className="identity-meta-item identity-id">
                <code title={user.id}>{user.id}</code>
                <CopyIdButton value={user.id} variant="icon" />
              </span>
            </div>
          </div>
          {(canMessage || canMerge) && (
            <div className="identity-actions">
              {canMessage && (
                <button
                  type="button"
                  className="ud-btn ud-btn-primary"
                  onClick={() => setChatModalOpen(true)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">send</span>
                  {t('dashboard.userDetails.messageTelegram')}
                </button>
              )}
              {canMerge && (
                <button
                  type="button"
                  className="ud-btn"
                  onClick={() => setMergeModalOpen(true)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">merge</span>
                  {t('dashboard.userDetails.mergeButton')}
                </button>
              )}
            </div>
          )}
        </header>

        <div className="ud-layout">
          {/* Account controls — beside the history on desktop, above it on mobile */}
          <aside className="ud-side">
            <section className="ud-panel" aria-labelledby="ud-balance-title">
              <h2 id="ud-balance-title" className="ud-panel-title">
                {t('dashboard.userDetails.balance')}
              </h2>
              <p className={`ud-balance-value ${balanceTone}`}>
                {formatNumericValue(user.balance)}
                <span className="ud-balance-currency">₴</span>
              </p>
              {user.balance < 0 && (
                <p className="ud-panel-hint">{t('dashboard.userDetails.balanceNegativeHint')}</p>
              )}
              <button
                type="button"
                className="ud-btn ud-btn-block"
                onClick={() => setBalanceModalOpen(true)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                {t('dashboard.userDetails.editBalance')}
              </button>
            </section>

            <section className="ud-panel" aria-labelledby="ud-status-title">
              <h2 id="ud-status-title" className="ud-panel-title">
                {t('dashboard.userDetails.statusTitle')}
              </h2>
              <UserStatusControl userId={user.id} status={user.status} />
            </section>

            {canManageUsers ? (
              <UserRolesCard userId={userId} initialRoleIds={user.roleIds ?? []} />
            ) : (
              <section className="ud-panel" aria-labelledby="ud-role-title">
                <h2 id="ud-role-title" className="ud-panel-title">
                  {t('dashboard.userDetails.rolesTitle')}
                </h2>
                <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                  {formatUserRole(user.role)}
                </span>
              </section>
            )}

            <UserOrganizationCard
              userId={user.id}
              organization={user.organization}
              canJoinOrganization={user.canJoinOrganization}
            />
          </aside>

          {/* History */}
          <section className="ud-panel ud-history">
            <nav
              className="user-details-tabs"
              role="tablist"
              aria-label={t('dashboard.userDetails.userHistoryAria')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'sessions'}
                className={`user-details-tab ${activeTab === 'sessions' ? 'active' : ''}`}
                onClick={() => setActiveTab('sessions')}
              >
                {t('dashboard.userDetails.sessionsTab')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'transactions'}
                className={`user-details-tab ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                {t('dashboard.userDetails.transactionsTab')}
              </button>
            </nav>
            <div role="tabpanel">
              {activeTab === 'sessions' ? (
                <SessionsPanel userId={userId} pagination={sessionsPagination} />
              ) : (
                <TransactionsPanel userId={userId} pagination={transactionsPagination} />
              )}
            </div>
          </section>
        </div>
      </div>

      {isBalanceModalOpen && (
        <BalanceModal
          userId={userId}
          currentBalance={user.balance}
          onClose={() => setBalanceModalOpen(false)}
        />
      )}

      {isChatModalOpen && (
        <StartChatModal userId={userId} onClose={() => setChatModalOpen(false)} />
      )}

      {isMergeModalOpen && (
        <MergeModal
          survivorId={userId}
          survivorEmail={user.email}
          survivorPhone={user.phone}
          survivorBalance={user.balance}
          onClose={() => setMergeModalOpen(false)}
        />
      )}
    </div>
  );
});

export default UserDetails;
